import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTrashCan, faXmark, faPlus } from '@fortawesome/free-solid-svg-icons';

// Dashboard-only instrument identifiers are intentionally fixed.
const DASHBOARD_INSTRUMENTS = [
  { symbol: 'NIFTY 50', exchange: 'NSE CM', segment: 'nse_cm', token: '26000', quoteInstrument: 'Nifty 50' },
  { symbol: 'BANKNIFTY', exchange: 'NSE CM', segment: 'nse_cm', token: '26009', quoteInstrument: 'Nifty Bank' },
  { symbol: 'FINNIFTY', exchange: 'NSE CM', segment: 'nse_cm', token: '26037', quoteInstrument: 'Nifty Fin Service' },
  { symbol: 'MIDCPNIFTY', exchange: 'NSE CM', segment: 'nse_cm', token: '26074', quoteInstrument: 'NIFTY MID SELECT' },
  { symbol: 'SENSEX', exchange: 'BSE CM', segment: 'bse_cm', token: '1', quoteInstrument: 'SENSEX' },
  { symbol: 'BANKEX', exchange: 'BSE CM', segment: 'bse_cm', token: '12', quoteInstrument: 'BANKEX' },
];

const clean = (value) => String(value ?? '').trim();
const numberValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const quoteLtp = (quote) => numberValue(quote?.ltp ?? quote?.last_price ?? quote?.lastPrice ?? quote?.lp ?? quote?.LTP);
const quoteChange = (quote) => numberValue(quote?.change ?? quote?.net_change ?? quote?.chg ?? quote?.change_value);
const quotePercent = (quote) => numberValue(quote?.per_change ?? quote?.perChange ?? quote?.percent_change ?? quote?.change_percent);
const quoteField = (quote, ...keys) => {
  for (const key of keys) {
    const value = quote?.[key];
    if (value !== undefined && value !== null && clean(value) !== '') return value;
  }
  return null;
};
const quoteToken = (quote) => clean(quote?.exchange_token ?? quote?.exchangeToken ?? quote?.token ?? quote?.symbol);
const flattenQuotes = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenQuotes);
  if (typeof value !== 'object') return [];
  const token = quoteToken(value);
  if (token) return [value];
  return Object.values(value).flatMap(flattenQuotes);
};
const extractQuotes = (payload) => {
  const candidates = [payload, payload?.data, payload?.data?.data, payload?.data?.data?.data];
  for (const candidate of candidates) {
    const quotes = flattenQuotes(candidate);
    if (quotes.length) return quotes;
  }
  return [];
};
const formatNumber = (value, decimals = 2) => {
  const n = numberValue(value);
  if (n === null) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const formatChange = (value) => {
  const n = numberValue(value);
  if (n === null) return '—';
  return `${n >= 0 ? '+' : ''}${formatNumber(n)}`;
};
const formatPercent = (value) => {
  const n = numberValue(value);
  if (n === null) return '—';
  return `${n >= 0 ? '+' : ''}${formatNumber(n)}%`;
};
const rangePosition = (quote) => {
  const ltp = quoteLtp(quote);
  const low = numberValue(quoteField(quote?.ohlc || {}, 'low'));
  const high = numberValue(quoteField(quote?.ohlc || {}, 'high'));
  if (ltp === null || low === null || high === null || high <= low) return 50;
  return Math.min(100, Math.max(0, ((ltp - low) / (high - low)) * 100));
};

const accountKey = () => localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default';

function DashboardAlertModal({ item, quote, alerts, onClose, onCreated, API }) {
  const [condition, setCondition] = useState('ABOVE');
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const itemAlerts = alerts.filter(a => String(a.exchangeSegment) === String(item.segment) && String(a.instrumentToken) === String(item.token));

  const createAlert = async () => {
    const value = Number(target);
    if (!Number.isFinite(value) || value <= 0 || saving) {
      setMessage('Enter a valid target price.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const response = await axios.post(`${API}/api/alerts`, {
        accountKey: accountKey(),
        exchangeSegment: item.segment,
        instrumentToken: item.token,
        symbol: item.symbol,
        conditionType: condition,
        targetPrice: value,
      });
      onCreated(response.data);
      setTarget('');
      setMessage('Alert added. You can add another alert.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to create alert.');
    } finally {
      setSaving(false);
    }
  };

  const removeAlert = async (id) => {
    try {
      await axios.delete(`${API}/api/alerts/${id}`, { params: { accountKey: accountKey() } });
      onCreated(null, id);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Unable to remove alert.');
    }
  };

  return (
    <div className="dashboard-alert-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="dashboard-alert-modal" role="dialog" aria-modal="true" aria-label={`${item.symbol} price alerts`}>
        <header className="dashboard-alert-header">
          <div>
            <small>PRICE ALERT</small>
            <h3>{item.symbol}</h3>
            <span>{item.exchange} · LTP ₹ {formatNumber(quoteLtp(quote))}</span>
          </div>
          <button type="button" className="dashboard-alert-close" onClick={onClose} aria-label="Close"><FontAwesomeIcon icon={faXmark} /></button>
        </header>

        <div className="dashboard-alert-form">
          <div className="dashboard-alert-form-grid">
            <label><span>Condition</span><select value={condition} onChange={e => setCondition(e.target.value)}><option value="ABOVE">Above / reaches</option><option value="BELOW">Below / reaches</option><option value="CROSS_ABOVE">Cross above</option><option value="CROSS_BELOW">Cross below</option></select></label>
            <label><span>Target Price</span><input type="number" inputMode="decimal" min="0" step="0.01" value={target} onChange={e => setTarget(e.target.value)} placeholder="Enter price" /></label>
          </div>
          <button type="button" className="dashboard-alert-add" disabled={saving} onClick={createAlert}><FontAwesomeIcon icon={faPlus} /> {saving ? 'Adding...' : 'Add Alert'}</button>
          {message && <small className="dashboard-alert-message">{message}</small>}
        </div>

        <div className="dashboard-alert-list-head"><b>Alerts</b><span>{itemAlerts.filter(a => a.status === 'ACTIVE').length} active · {itemAlerts.length} total</span></div>
        <div className="dashboard-alert-list">
          {!itemAlerts.length ? <div className="dashboard-alert-empty">No alerts for this index. Add your first price alert above.</div> : itemAlerts.map(alert => (
            <div className={`dashboard-alert-row ${alert.status === 'TRIGGERED' ? 'triggered' : ''}`} key={alert.id}>
              <div><b>{String(alert.conditionType || '').replaceAll('_', ' ')}</b><strong>₹ {Number(alert.targetPrice).toLocaleString('en-IN', { maximumFractionDigits: 4 })}</strong><small>{alert.status}</small></div>
              <button type="button" onClick={() => removeAlert(alert.id)} title="Delete alert" aria-label="Delete alert"><FontAwesomeIcon icon={faTrashCan} /></button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const MarketCard = ({ item, quote, alerts, onOpenAlert }) => {
  const change = quoteChange(quote);
  const percent = quotePercent(quote);
  const positive = (change ?? percent ?? 0) >= 0;
  const ohlc = quote?.ohlc || {};
  const low = quoteField(ohlc, 'low');
  const high = quoteField(ohlc, 'high');
  const prevClose = quoteField(ohlc, 'close', 'prev_close', 'prevClose');
  const activeAlertCount = alerts.filter(a => String(a.exchangeSegment) === String(item.segment) && String(a.instrumentToken) === String(item.token) && a.status === 'ACTIVE').length;

  return (
    <article className="dashboard-market-card">
      <div className="dashboard-market-head">
        <div><h3>{item.symbol}</h3><span>{item.exchange} <i>•</i> {item.token}</span></div>
        <span className={`dashboard-market-status ${positive ? 'positive' : 'negative'}`}>{positive ? '▲' : '▼'}</span>
      </div>
      <div className="dashboard-market-price-row"><strong>₹ {formatNumber(quoteLtp(quote))}</strong><div className={positive ? 'positive' : 'negative'}><b>{formatChange(change)}</b><span>({formatPercent(percent)})</span></div></div>
      <div className="dashboard-range-card" aria-label="Today's high low range">
        <div className="dashboard-range-labels"><div><span>Today's Low</span><b>{formatNumber(low)}</b></div><div className="dashboard-range-high"><span>Today's High</span><b>{formatNumber(high)}</b></div></div>
        <div className="dashboard-range-line"><i style={{ left: `${rangePosition(quote)}%` }} /></div>
      </div>
      <div className="dashboard-market-stats">
        <div><span>Open</span><b>{formatNumber(quoteField(ohlc, 'open'))}</b></div>
        <div><span>Prev. Close</span><b>{formatNumber(prevClose)}</b></div>
        <div className="dashboard-alert-stat"><span>Alert</span><button type="button" className={`dashboard-alert-icon ${activeAlertCount ? 'active' : ''}`} onClick={() => onOpenAlert(item)} title="Set price alert" aria-label={`Set price alert for ${item.symbol}`}><FontAwesomeIcon icon={faBell} />{activeAlertCount > 0 && <em>{activeAlertCount}</em>}</button></div>
      </div>
    </article>
  );
};

export default function Home() {
  const API = process.env.REACT_APP_API_URL || '';
  const [quoteMap, setQuoteMap] = useState({});
  const [quoteError, setQuoteError] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertItem, setAlertItem] = useState(null);
  const timerRef = useRef(null);

  const loadAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/alerts`, { params: { accountKey: accountKey() } });
      setAlerts(response.data?.alerts || []);
    } catch (error) {
      console.error('Dashboard alerts load error:', error.message);
    }
  }, [API]);

  const fetchDashboardQuotes = useCallback(async () => {
    const baseUrl = (localStorage.getItem('kotak_baseurl') || '').replace(/\/$/, '');
    const accessToken = localStorage.getItem('kotak_access_token') || '';
    if (!baseUrl || !accessToken) { setQuoteError('Trading session is not active. Please login again.'); setQuoteLoading(false); return; }
    try {
      const symbols = DASHBOARD_INSTRUMENTS.map(item => `${item.segment}|${item.quoteInstrument}`).join(',');
      const response = await axios.get(`${API}/api/kotak-quotes`, { params: { symbols, baseUrl }, headers: { Authorization: accessToken } });
      const nextMap = {};
      extractQuotes(response.data).forEach((quote) => {
        const token = quoteToken(quote);
        const display = clean(quote?.display_symbol || quote?.trading_symbol || quote?.symbol || quote?.pTrdSymbol).toLowerCase();
        const matched = DASHBOARD_INSTRUMENTS.find((item) => [item.token, item.quoteInstrument, item.symbol].map(v => clean(v).toLowerCase()).some(v => v === token.toLowerCase() || v === display));
        if (matched) nextMap[matched.token] = quote; else if (token) nextMap[token] = quote;
      });
      if (Object.keys(nextMap).length) { setQuoteMap(nextMap); setQuoteError(''); } else setQuoteError('No live market quote data received.');
    } catch (error) {
      console.error('Dashboard market quote error:', error);
      setQuoteError(error.response?.data?.error || 'Unable to load live market quotes.');
    } finally { setQuoteLoading(false); }
  }, [API]);

  useEffect(() => { fetchDashboardQuotes(); timerRef.current = window.setInterval(fetchDashboardQuotes, 1000); return () => window.clearInterval(timerRef.current); }, [fetchDashboardQuotes]);
  useEffect(() => { loadAlerts(); const timer = window.setInterval(loadAlerts, 5000); return () => window.clearInterval(timer); }, [loadAlerts]);

  const handleAlertCreated = (created, removedId) => {
    if (removedId) setAlerts(current => current.filter(a => Number(a.id) !== Number(removedId)));
    else if (created) setAlerts(current => [created, ...current]);
  };

  return (
    <main className="terminal-page dashboard-page">
      <section className="dashboard-section-head"><div><span>MARKET</span><h1>Market Overview</h1></div><small>{quoteLoading ? 'Loading live quotes…' : quoteError ? quoteError : 'Live • Updates every 1s'}</small></section>
      <section className="dashboard-market-grid" aria-label="Live market quotes">{DASHBOARD_INSTRUMENTS.map(item => <MarketCard key={item.token} item={item} quote={quoteMap[item.token]} alerts={alerts} onOpenAlert={setAlertItem} />)}</section>
      <section className="dashboard-summary-grid"><div className="terminal-card dashboard-summary-card"><span>Market Status</span><strong><i /> Live Quotes</strong><small>Dashboard uses the existing Kotak market-quote API.</small></div><div className="terminal-card dashboard-summary-card"><span>Tracked Indices</span><strong>06</strong><small>NIFTY 50, BANKNIFTY, FINNIFTY, MIDCPNIFTY, SENSEX &amp; BANKEX</small></div></section>
      {alertItem && <DashboardAlertModal item={alertItem} quote={quoteMap[alertItem.token]} alerts={alerts} onClose={() => setAlertItem(null)} onCreated={handleAlertCreated} API={API} />}
    </main>
  );
}
